/* iter156dz: Modulo de Ordens de Servico / Locacao (v1)
   - Lista OS por filial + status
   - Criar/Editar OS com produtos + servicos
   - Mudar status (open -> in_progress -> finished, cancel a qualquer momento)
   - Search remoto de clientes e produtos/servicos */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Play, CheckCircle, XCircle, Package, User, Calendar, FileText, X, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ERP = `${API}/erp`;

const STATUS_META = {
  open: { label: 'Aberta', color: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'bg-slate-400' },
  in_progress: { label: 'Em andamento', color: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500' },
  finished: { label: 'Finalizada', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelada', color: 'bg-rose-100 text-rose-700 border-rose-300', dot: 'bg-rose-500' },
};

const fmtBRL = (n) => (Number(n || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ServiceOrdersPage = ({ companyId, companies = [], moduleLabel = 'Ordem de Servico' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  // iter156ec: modal de finalizacao com baixa financeira
  const [finishing, setFinishing] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: companyId });
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const { data } = await axios.get(`${ERP}/service-orders?${params.toString()}`, { withCredentials: true });
      setItems(data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao carregar OS');
    } finally { setLoading(false); }
  }, [companyId, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status, reason = '') => {
    try {
      await axios.put(`${ERP}/service-orders/${id}/status`, { status, reason }, { withCredentials: true });
      toast.success(`Status alterado para ${STATUS_META[status]?.label || status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao mudar status');
    }
  };

  const removeOS = async (os) => {
    if (!window.confirm(`Excluir OS #${os.number}? (so OS abertas ou canceladas podem ser removidas)`)) return;
    try {
      await axios.delete(`${ERP}/service-orders/${os.id}`, { withCredentials: true });
      toast.success('OS excluida');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao excluir');
    }
  };

  const companyName = useMemo(() => companies.find(c => c.id === companyId)?.name || '', [companies, companyId]);

  return (
    <div className="space-y-4" data-testid="service-orders-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">{moduleLabel}</h1>
          <p className="text-xs text-slate-500 mt-0.5">{companyName ? `Filial: ${companyName}` : 'Selecione uma filial'}</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nome do cliente ou nº..." className="pl-8 pr-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-search" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-status-filter">
            <option value="">Todos os status</option>
            <option value="open">Abertas</option>
            <option value="in_progress">Em andamento</option>
            <option value="finished">Finalizadas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          <button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!companyId} className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg" data-testid="os-new-btn">
            <Plus size={14} /> Nova OS
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400 text-sm">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-lg">
          <FileText size={40} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">Nenhuma OS encontrada.</p>
          <p className="text-slate-400 text-xs mt-1">Clique em "Nova OS" para comecar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(os => {
            const st = STATUS_META[os.status] || STATUS_META.open;
            return (
              <div key={os.id} className="bg-white border-2 border-slate-200 rounded-lg p-3 hover:border-violet-300 transition" data-testid={`os-card-${os.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase">OS Nº</p>
                    <p className="text-xl font-black text-slate-800 font-mono">{String(os.number).padStart(5, '0')}</p>
                  </div>
                  <span className={`text-[10px] font-black px-2 py-1 rounded-full border ${st.color}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${st.dot} mr-1`}></span>{st.label}
                  </span>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div className="flex items-center gap-1.5"><User size={11} className="text-slate-400" /><span className="truncate">{os.client_name || '— sem cliente —'}</span></div>
                  {(os.start_date || os.end_date) && (
                    <div className="flex items-center gap-1.5"><Calendar size={11} className="text-slate-400" />
                      <span>{os.start_date || '—'} {os.end_date ? `-> ${os.end_date}` : ''}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5"><Package size={11} className="text-slate-400" /><span>{os.items?.length || 0} item(s)</span></div>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-sm font-black text-emerald-700">{fmtBRL(os.total)}</p>
                  <div className="flex gap-1">
                    {os.status === 'open' && (
                      <button onClick={() => changeStatus(os.id, 'in_progress')} title="Iniciar" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" data-testid={`os-start-${os.id}`}><Play size={13} /></button>
                    )}
                    {os.status === 'in_progress' && (
                      <button onClick={() => setFinishing(os)} title="Finalizar (com baixa financeira)" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`os-finish-${os.id}`}><CheckCircle size={13} /></button>
                    )}
                    {(os.status === 'open' || os.status === 'in_progress') && (
                      <button onClick={() => {
                        const r = window.prompt('Motivo do cancelamento (opcional):');
                        if (r === null) return;
                        changeStatus(os.id, 'cancelled', r);
                      }} title="Cancelar" className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`os-cancel-${os.id}`}><XCircle size={13} /></button>
                    )}
                    {(os.status === 'open' || os.status === 'cancelled') && (
                      <>
                        <button onClick={() => { setEditing(os); setShowForm(true); }} title="Editar" className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" data-testid={`os-edit-${os.id}`}><Edit size={13} /></button>
                        <button onClick={() => removeOS(os)} title="Excluir" className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`os-delete-${os.id}`}><Trash2 size={13} /></button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ServiceOrderFormModal
          os={editing}
          companyId={companyId}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
      {finishing && (
        <FinishOSModal
          os={finishing}
          companyId={companyId}
          onClose={() => setFinishing(null)}
          onSaved={() => { setFinishing(null); load(); }}
        />
      )}
    </div>
  );
};


// iter156ec: modal para finalizar OS com baixa financeira opcional.
// Permite: sem financeiro (pula), a vista, parcelado (N x com intervalo).
const FinishOSModal = ({ os, companyId, onClose, onSaved }) => {
  const [chart, setChart] = useState('');
  const [bank, setBank] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [installments, setInstallments] = useState(1);
  const [installmentDays, setInstallmentDays] = useState(30);
  const [firstDue, setFirstDue] = useState(() => new Date().toISOString().slice(0, 10));
  const [genFinancial, setGenFinancial] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [ca, ba, pm] = await Promise.all([
          axios.get(`${ERP}/chart-accounts`, { withCredentials: true }),
          axios.get(`${ERP}/bank-accounts?company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/payment-methods?scope=financial&company_id=${companyId}`, { withCredentials: true }),
        ]);
        const chartList = (ca.data.data || []).filter(c => ['income', 'asset'].includes(c.type));
        setChartAccounts(chartList);
        setBankAccounts(ba.data.data || []);
        setPaymentMethods(pm.data.data || pm.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, [companyId]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { status: 'finished' };
      if (genFinancial) {
        if (!chart || !bank) { toast.error('Selecione Plano de Contas e Conta Banco'); setSaving(false); return; }
        payload.chart_account_id = chart;
        payload.bank_account_id = bank;
        payload.payment_method = payMethod;
        payload.installments = Math.max(1, parseInt(installments, 10) || 1);
        payload.installment_days = Math.max(0, parseInt(installmentDays, 10) || 30);
        payload.first_due_date = firstDue || null;
      }
      await axios.put(`${ERP}/service-orders/${os.id}/status`, payload, { withCredentials: true });
      toast.success(genFinancial ? 'OS finalizada e titulos gerados' : 'OS finalizada (sem baixa financeira)');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao finalizar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="finish-os-modal">
        <div className="px-4 py-3 border-b flex items-center justify-between bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
          <div>
            <h2 className="font-black text-lg">Finalizar OS #{os.number}</h2>
            <p className="text-[11px] opacity-90">Total: {(Number(os.total || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} · Cliente: {os.client_name || '—'}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="finish-os-close"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <label className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
            <input type="checkbox" checked={genFinancial} onChange={e => setGenFinancial(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid="finish-os-gen-financial" />
            <div>
              <span className="text-sm font-black text-emerald-800">Gerar titulos no Financeiro</span>
              <p className="text-[10px] text-slate-600">Desmarque para so mudar o status sem gerar contas a receber</p>
            </div>
          </label>
          {genFinancial && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">* Plano de Contas</label>
                <select value={chart} onChange={e => setChart(e.target.value)} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="finish-os-chart">
                  <option value="">Selecione</option>
                  {chartAccounts.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">* Conta Banco</label>
                <select value={bank} onChange={e => setBank(e.target.value)} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="finish-os-bank">
                  <option value="">Selecione</option>
                  {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.name || b.bank_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="finish-os-pm">
                  <option value="">— Selecione —</option>
                  {paymentMethods.map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Nº Parcelas</label>
                  <input type="number" min="1" max="60" value={installments} onChange={e => setInstallments(e.target.value)} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-center" data-testid="finish-os-installments" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Intervalo (dias)</label>
                  <input type="number" min="0" max="365" value={installmentDays} onChange={e => setInstallmentDays(e.target.value)} disabled={installments <= 1} className={`w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-center ${installments <= 1 ? 'bg-slate-100 opacity-60' : ''}`} data-testid="finish-os-installment-days" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">1ª Vencimento</label>
                  <input type="date" value={firstDue} onChange={e => setFirstDue(e.target.value)} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="finish-os-first-due" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                {installments == 1 ? '1 titulo a vista com o valor total.' : `${installments} parcelas de aprox. ${(Number(os.total||0) / installments).toLocaleString('pt-BR', {style:'currency',currency:'BRL'})} cada, com intervalo de ${installmentDays} dia(s).`}
              </p>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-bold hover:bg-slate-100 rounded-lg" data-testid="finish-os-cancel">Cancelar</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg flex items-center gap-1.5" data-testid="finish-os-submit">
            <DollarSign size={14} />
            {saving ? 'Finalizando...' : 'Finalizar OS'}
          </button>
        </div>
      </div>
    </div>
  );
};


const ServiceOrderFormModal = ({ os, companyId, onClose, onSaved }) => {
  const isEdit = !!os;
  const [form, setForm] = useState({
    client_id: os?.client_id || '',
    client_name: os?.client_name || '',
    client_phone: os?.client_phone || '',
    start_date: os?.start_date || '',
    end_date: os?.end_date || '',
    notes: os?.notes || '',
  });
  const [items, setItems] = useState(os?.items?.length ? os.items : []);
  const [saving, setSaving] = useState(false);

  // Search state: cliente e produto
  const [clientSearch, setClientSearch] = useState('');
  const [clientOpts, setClientOpts] = useState([]);
  const [prodSearch, setProdSearch] = useState('');
  const [prodOpts, setProdOpts] = useState([]);
  const [prodSearching, setProdSearching] = useState(false);

  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientOpts([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${ERP}/clients?search=${encodeURIComponent(clientSearch)}&limit=15&company_id=${encodeURIComponent(companyId)}`, { withCredentials: true });
        setClientOpts(data.data || []);
      } catch { setClientOpts([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [clientSearch, companyId]);

  useEffect(() => {
    if (!prodSearch || prodSearch.length < 2) { setProdOpts([]); return; }
    const t = setTimeout(async () => {
      setProdSearching(true);
      try {
        const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(prodSearch)}&limit=15`, { withCredentials: true });
        setProdOpts(data.data || data || []);
      } catch { setProdOpts([]); }
      finally { setProdSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [prodSearch]);

  const addItem = (p) => {
    setItems(prev => [...prev, {
      product_id: p.id,
      name: p.name,
      is_service: !!p.is_service,
      quantity: 1,
      unit_price: Number(p.price || 0),
      total: Number(p.price || 0),
      notes: '',
    }]);
    setProdSearch('');
    setProdOpts([]);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const upd = { ...it, [field]: value };
      const q = Number(upd.quantity || 0);
      const p = Number(upd.unit_price || 0);
      upd.total = Number((q * p).toFixed(2));
      return upd;
    }));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const total = useMemo(() => items.reduce((s, i) => s + Number(i.total || 0), 0), [items]);

  const submit = async () => {
    if (!companyId) return toast.error('Filial ausente');
    if (items.length === 0) return toast.error('Adicione ao menos 1 item na OS');
    setSaving(true);
    try {
      const payload = {
        company_id: companyId,
        client_id: form.client_id,
        client_name: form.client_name,
        client_phone: form.client_phone,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes,
        items: items.map(i => ({
          product_id: i.product_id,
          name: i.name,
          is_service: !!i.is_service,
          quantity: Number(i.quantity || 0),
          unit_price: Number(i.unit_price || 0),
          total: Number(i.total || 0),
          notes: i.notes || '',
        })),
      };
      if (isEdit) {
        await axios.put(`${ERP}/service-orders/${os.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${ERP}/service-orders`, payload, { withCredentials: true });
      }
      toast.success(isEdit ? 'OS atualizada' : 'OS criada');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="os-form-modal">
        <div className="px-4 py-3 border-b flex items-center justify-between bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white">
          <div>
            <h2 className="font-black text-lg">{isEdit ? `Editar OS #${os.number}` : 'Nova Ordem de Servico'}</h2>
            <p className="text-[11px] opacity-80">{isEdit ? 'Alteracoes preservam o numero da OS' : 'Vincula produtos + servicos (ex: locacao, mao de obra)'}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="os-close-btn"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Cliente */}
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Cliente</label>
            <div className="relative">
              <input value={form.client_name} onChange={e => { setForm({ ...form, client_name: e.target.value, client_id: '' }); setClientSearch(e.target.value); }} placeholder="Nome do cliente (digite 2+ letras para buscar)" className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-client-name" />
              {clientOpts.length > 0 && (
                <div className="absolute z-10 top-full left-0 right-0 bg-white border-2 border-violet-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  {clientOpts.map(c => (
                    <button key={c.id} onClick={() => { setForm({ ...form, client_id: c.id, client_name: c.name, client_phone: c.phone || form.client_phone }); setClientOpts([]); setClientSearch(''); }} className="block w-full text-left px-3 py-2 text-xs hover:bg-violet-50" data-testid={`os-client-opt-${c.id}`}>
                      <div className="font-semibold">{c.name}</div>
                      {c.document && <div className="text-[10px] text-slate-500">{c.document}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Telefone</label>
              <input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} placeholder="(00) 00000-0000" className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-client-phone" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Data Inicio</label>
              <input type="date" value={form.start_date || ''} onChange={e => setForm({ ...form, start_date: e.target.value })} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-start-date" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Data Fim / Devolucao</label>
              <input type="date" value={form.end_date || ''} onChange={e => setForm({ ...form, end_date: e.target.value })} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-end-date" />
            </div>
          </div>

          {/* Itens */}
          <div className="border-t border-slate-100 pt-3">
            <h3 className="text-xs font-black text-slate-700 uppercase mb-2">Itens da OS</h3>
            <div className="mb-2 relative">
              <input value={prodSearch} onChange={e => setProdSearch(e.target.value)} placeholder="Buscar produto ou servico (digite 2+ letras)" className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" data-testid="os-item-search" />
              {(prodOpts.length > 0 || prodSearching) && (
                <div className="absolute z-10 top-full left-0 right-0 bg-white border-2 border-violet-300 rounded-lg shadow-xl max-h-52 overflow-y-auto mt-1">
                  {prodSearching && <div className="text-center py-2 text-[10px] text-violet-500 italic">Buscando...</div>}
                  {prodOpts.map(p => (
                    <button key={p.id} onClick={() => addItem(p)} className="block w-full text-left px-3 py-2 text-xs hover:bg-violet-50 border-b border-slate-50 last:border-0" data-testid={`os-item-opt-${p.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold truncate">
                            {p.is_service && <span className="inline-block text-[9px] font-black px-1 py-0.5 bg-amber-100 text-amber-700 rounded mr-1">SERVICO</span>}
                            {p.name}
                          </div>
                          <div className="text-[10px] text-slate-500">SKU: {p.sku}</div>
                        </div>
                        <div className="text-xs font-mono text-emerald-700">{fmtBRL(p.price)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {items.length === 0 ? (
              <p className="text-center text-slate-400 text-xs py-4">Nenhum item adicionado. Use a busca acima.</p>
            ) : (
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className={`p-2 border-2 rounded-lg ${it.is_service ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`os-item-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_80px_100px_100px_auto] gap-2 items-end">
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase">{it.is_service ? 'Servico' : 'Produto'}</label>
                        <p className="text-xs font-semibold text-slate-800 truncate">{it.name}</p>
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase">Qtde</label>
                        <input type="number" step="0.01" min="0" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono text-right" data-testid={`os-item-qty-${idx}`} />
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase">Preco Unit.</label>
                        <input type="number" step="0.01" min="0" value={it.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono text-right" data-testid={`os-item-price-${idx}`} />
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase">Total</label>
                        <p className="text-xs font-mono font-black text-emerald-700 text-right">{fmtBRL(it.total)}</p>
                      </div>
                      <button onClick={() => removeItem(idx)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`os-item-remove-${idx}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Observacoes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm resize-none" data-testid="os-notes" />
          </div>
        </div>
        <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
          <p className="text-sm font-black text-emerald-700">Total: {fmtBRL(total)}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-bold hover:bg-slate-100 rounded-lg" data-testid="os-cancel-btn">Cancelar</button>
            <button onClick={submit} disabled={saving} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg" data-testid="os-save-btn">
              {saving ? 'Salvando...' : (isEdit ? 'Atualizar OS' : 'Criar OS')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceOrdersPage;
